import React, { useState } from "react";
import { Phone, MapPin, MessageCircle, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import { brand, quoteOptions } from "../mock";
import { Reveal } from "../hooks/useReveal";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    interest: quoteOptions[0],
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    // MOCK: store the enquiry locally (no backend yet)
    const saved = JSON.parse(localStorage.getItem("af_quotes") || "[]");
    saved.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("af_quotes", JSON.stringify(saved));
    toast.success("Thank you! We'll respond within 24 hours.");
    setForm({
      name: "",
      phone: "",
      email: "",
      interest: quoteOptions[0],
      message: "",
    });
  };

  const inputCls =
    "w-full bg-[#0c0d0f] border border-[#2a2d34] rounded-xl px-4 py-3.5 text-white placeholder:text-[var(--af-muted)] focus:outline-none focus:border-[var(--af-blue)] transition-colors";

  return (
    <>
      <section className="relative pt-44 pb-20 bg-[#0a0b0d] border-b border-[#1c1e23] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 40% 20%, var(--af-blue), transparent 45%)",
          }}
        />
        <div className="af-container relative z-10">
          <p className="eyebrow mb-5">Contact</p>
          <h1 className="font-display font-bold text-white text-5xl md:text-8xl leading-[0.95]">
            Let’s Build Something Beautiful.
          </h1>
          <p className="mt-6 text-lg text-[var(--af-muted)] max-w-2xl">
            Book a free consultation — we respond within 24 hours.
          </p>
        </div>
      </section>

      <section className="py-20 bg-[#0c0d0f]">
        <div className="af-container grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <Reveal>
            <div className="p-8 md:p-10 rounded-3xl bg-[#121317] border border-[#1e2026]">
              <h2 className="font-display font-semibold text-3xl text-white mb-2">
                Get a Free Quote
              </h2>
              <p className="text-[var(--af-muted)] mb-8">
                Tell us about your project. No obligation, no pressure.
              </p>
              <form onSubmit={submit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">
                      Full Name *
                    </label>
                    <input
                      className={inputCls}
                      placeholder="Your name"
                      value={form.name}
                      onChange={update("name")}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-white/70 mb-2 block">
                      Phone *
                    </label>
                    <input
                      className={inputCls}
                      placeholder="+91 ..."
                      value={form.phone}
                      onChange={update("phone")}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-2 block">
                    Email
                  </label>
                  <input
                    className={inputCls}
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={update("email")}
                  />
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-2 block">
                    I'm interested in
                  </label>
                  <select
                    className={`${inputCls} appearance-none cursor-pointer`}
                    value={form.interest}
                    onChange={update("interest")}
                  >
                    {quoteOptions.map((o) => (
                      <option key={o} value={o} className="bg-[#0c0d0f]">
                        {o}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-white/70 mb-2 block">
                    Project details
                  </label>
                  <textarea
                    rows={4}
                    className={`${inputCls} resize-none`}
                    placeholder="Tell us about your space, sizes, timeline..."
                    value={form.message}
                    onChange={update("message")}
                  />
                </div>
                <button
                  type="submit"
                  className="group w-full inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#0c0d0f] font-semibold rounded-full hover:bg-[var(--af-blue)] hover:text-white transition-all"
                >
                  Request My Free Quote
                  <Send
                    size={17}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </button>
              </form>
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={120}>
            <div className="space-y-6">
              <a
                href={brand.mapUrl}
                target="_blank"
                rel="noreferrer"
                className="card-lift block p-8 rounded-3xl bg-[#121317] border border-[#1e2026] hover:border-[var(--af-blue)]/40"
              >
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="text-[var(--af-blue)]" size={22} />
                  <h3 className="font-display font-semibold text-2xl text-white">
                    Visit Our Showroom
                  </h3>
                </div>
                <p className="text-[var(--af-muted)] leading-relaxed mb-4">
                  {brand.address}
                </p>
                <span className="inline-flex items-center gap-1 text-[var(--af-blue)] hover-underline">
                  Get Directions <ArrowUpRight size={16} />
                </span>
              </a>

              <a
                href={brand.phoneHref}
                className="card-lift block p-8 rounded-3xl bg-[#121317] border border-[#1e2026] hover:border-[var(--af-blue)]/40"
              >
                <div className="flex items-center gap-3 mb-2">
                  <Phone className="text-[var(--af-blue)]" size={22} />
                  <h3 className="font-display font-semibold text-2xl text-white">
                    Call Us
                  </h3>
                </div>
                <p className="text-[var(--af-muted)]">{brand.phone}</p>
              </a>

              <a
                href={brand.whatsapp}
                target="_blank"
                rel="noreferrer"
                className="card-lift block p-8 rounded-3xl bg-[#121317] border border-[#1e2026] hover:border-[var(--af-green)]/50"
              >
                <div className="flex items-center gap-3 mb-2">
                  <MessageCircle className="text-[var(--af-green)]" size={22} />
                  <h3 className="font-display font-semibold text-2xl text-white">
                    WhatsApp
                  </h3>
                </div>
                <p className="text-[var(--af-muted)] flex items-center gap-1">
                  Chat with us instantly <ArrowUpRight size={15} />
                </p>
              </a>

              <div className="rounded-3xl overflow-hidden border border-[#1e2026] h-56">
                <iframe
                  title="August Doors & Windows location"
                  src="https://www.google.com/maps?q=Rambagh,Darbhanga,Bihar&output=embed"
                  className="w-full h-full grayscale contrast-125"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
